import React, { useState, useEffect, useMemo } from 'react';
import StaffLayout from '../../components/StaffLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Zap,
    Settings,
    DollarSign,
    XCircle,
    Calendar,
    Clock,
    ArrowRight,
    History,
    CheckCircle2,
    AlertTriangle,
    Play,
    Plus,
    Trash2,
    ChevronRight
} from 'lucide-react';
import { cn } from '@/lib/utils';
import lmsApi from '../../services/api';
import { API_BASE_URL } from '@/config';
import { toast } from 'sonner';
import { addDays, startOfDay, addHours } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import PaymentModal from '../../components/PaymentModal';

interface SimState {
    loan_id: string;
    as_of: string;
    t_plus: number;
    pos: number;
    dpd: number;
    principal_due: number;
    interest_due: number;
    fees_due: number;
    overdue_principal: number;
    overdue_interest: number;
    overdue_fees: number;
    overdue_total: number;
    total_due: number;
    total_outstanding: number;
    is_overdue: boolean;
    regular_installment: number;
    status: string;
    has_demand?: boolean;
}

interface LogItem {
    type: string;
    date: string;
    detail: string;
    cause: string;
    timestamp: string;
}

interface SimLoan {
    loan_id: string;
    amount: number;
    date: string;
    customer: string;
}

const SimulationDashboard = () => {
    const [loanId, setLoanId] = useState<string | null>(localStorage.getItem('current_sim_loan_id'));
    const [loanList, setLoanList] = useState<SimLoan[]>([]);
    const [liveState, setLiveState] = useState<SimState | null>(null);
    const [viewState, setViewState] = useState<SimState | null>(null);
    const [logs, setLogs] = useState<LogItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [eodProcessing, setEodProcessing] = useState(false);
    const [sodProcessing, setSodProcessing] = useState(false);
    const [showNewForm, setShowNewForm] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalAction, setModalAction] = useState<'PAY' | 'CANCEL' | 'FORECLOSE'>('PAY');

    // Sliders (top bar view scrubber)
    const [dayOffset, setDayOffset] = useState(0);
    const [timeOffset, setTimeOffset] = useState(10); // Default 10 AM

    // Demand Execution time (IST HH:MM) — replaces bottom EOD sliders
    const [demandTime, setDemandTime] = useState('20:00'); // Default 8PM IST

    // Simulation Params
    const [disburseAmount, setDisburseAmount] = useState('10000');
    const [roi, setRoi] = useState('12');
    const [tenure, setTenure] = useState('10');
    const [targetCustomerPhone, setTargetCustomerPhone] = useState('');

    // Planning
    const [demandStatus, setDemandStatus] = useState<'PASS' | 'FAIL'>('PASS');
    const [plannedEvents, setPlannedEvents] = useState({
        payment: { enabled: false, amount: '', time: '14:00' },
        cancel: { enabled: false, time: '12:00' },
        foreclose: { enabled: false, time: '16:00' }
    });

    const fetchLoanList = async () => {
        try {
            const response = await lmsApi.post('/sim/list/');
            setLoanList(response.data);
        } catch (err) {
            console.error('Failed to fetch loan list');
        }
    };

    const fetchUserInfo = async () => {
        try {
            const response = await lmsApi.post('/auth/get-user-phone/');
            if (response.data.phone_number) {
                setTargetCustomerPhone(response.data.phone_number);
            }
        } catch (err) {
            console.error('Failed to fetch user info');
        }
    };

    const fetchLiveState = async (id: string) => {
        try {
            const response = await lmsApi.post('/sim/state/', { loan_id: id });
            setLiveState(response.data);
            setDayOffset(response.data.t_plus);
            fetchLogs(id);
        } catch (err) {
            toast.error('Failed to fetch live state');
            setLoanId(null);
            localStorage.removeItem('current_sim_loan_id');
        }
    };

    const fetchLogs = async (id: string) => {
        const response = await lmsApi.post('/sim/logs/', { loan_id: id });
        setLogs(response.data);
    };

    useEffect(() => {
        fetchLoanList();
        fetchUserInfo();
        if (loanId) fetchLiveState(loanId);
    }, [loanId]);

    useEffect(() => {
        if (!loanId || !liveState) return;

        const fetchView = async () => {
            const disbursalStr = logs.find(l => l.detail.includes('SOD'))?.date || liveState.as_of;
            const baseDate = startOfDay(new Date(disbursalStr));
            const targetDate = addHours(addDays(baseDate, dayOffset), timeOffset);

            try {
                const response = await lmsApi.post(`/sim/state/`, {
                    loan_id: loanId,
                    as_of: targetDate.toISOString()
                });
                setViewState(response.data);
            } catch (err) {
                console.error('View fetch failed', err);
            }
        };

        fetchView();
    }, [dayOffset, timeOffset, loanId, liveState, logs]);

    const handleDisburse = async () => {
        setLoading(true);
        try {
            const response = await lmsApi.post(`/sim/disburse/`, {
                amount: parseFloat(disburseAmount),
                roi: parseFloat(roi),
                tenure: parseInt(tenure),
                phone: targetCustomerPhone || undefined
            });
            const newId = response.data.loan_id;
            setLoanId(newId);
            localStorage.setItem('current_sim_loan_id', newId);
            setShowNewForm(false);
            toast.success('Simulation Started');
        } catch (err) {
            toast.error('Initialization failed');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteLoan = async () => {
        if (!loanId) return;
        if (!confirm('Are you sure you want to delete this simulation? All history will be lost.')) return;

        try {
            await lmsApi.post(`/sim/delete/`, { loan_id: loanId });
            toast.success('Simulation Deleted');
            setLoanId(null);
            localStorage.removeItem('current_sim_loan_id');
            setLiveState(null);
            setViewState(null);
            setLogs([]);
            fetchLoanList();
        } catch (err) {
            toast.error('Deletion failed');
        }
    };

    const runEOD = async () => {
        if (!loanId) return;
        setEodProcessing(true);

        const events = [];
        if (plannedEvents.payment.enabled) {
            events.push({ type: 'PAYMENT', amount: plannedEvents.payment.amount, time: plannedEvents.payment.time });
        }
        if (plannedEvents.cancel.enabled) {
            events.push({ type: 'CANCEL', time: plannedEvents.cancel.time });
        }
        if (plannedEvents.foreclose.enabled) {
            events.push({ type: 'FORECLOSE', time: plannedEvents.foreclose.time });
        }

        try {
            await lmsApi.post(`/sim/eod/`, {
                loan_id: loanId,
                events,
                demand_status: demandStatus,
                demand_time: demandTime  // IST HH:MM — backend converts to UTC for transaction timestamp
            });
            setPlannedEvents({
                payment: { enabled: false, amount: '', time: '14:00' },
                cancel: { enabled: false, time: '12:00' },
                foreclose: { enabled: false, time: '16:00' }
            });
            await fetchLiveState(loanId);
            toast.success('EOD Processed. New Day Started.');
        } catch (err: any) {
            toast.error(err.response?.data?.error || 'EOD Failed');
        } finally {
            setEodProcessing(false);
        }
    };

    const runSOD = async () => {
        if (!loanId) return;
        setSodProcessing(true);
        try {
            await lmsApi.post(`/sim/sod/`, { loan_id: loanId });
            await fetchLiveState(loanId);
            toast.success('SOD Processed. Day Started.');
        } catch (err: any) {
            toast.error(err.response?.data?.error || 'SOD Failed');
        } finally {
            setSodProcessing(false);
        }
    };

    const handleImmediateAction = async (action: string) => {
        if (!loanId) return;
        setLoading(true);
        try {
            await lmsApi.post(`/sim/action/`, {
                loan_id: loanId,
                action,
                amount: action === 'PAY' ? (viewState?.total_outstanding || 0) : 0
            });
            toast.success(`${action} processed immediately`);
            await fetchLiveState(loanId);
        } catch (err) {
            toast.error('Action failed');
        } finally {
            setLoading(false);
        }
    };

    const isHistorical = useMemo(() => {
        if (!liveState) return false;
        return dayOffset < liveState.t_plus;
    }, [dayOffset, liveState]);

    return (
        <StaffLayout title="Simulation Dashboard">
            <div className="flex flex-col gap-4 h-full">

                {/* Top Header & Multi-Loan Management */}
                <div className="flex justify-between items-center bg-white p-4 rounded-xl shadow-sm border border-slate-100 h-[64px]">
                    <div className="flex items-center gap-4">
                        <Select value={loanId || ""} onValueChange={(val) => {
                            setLoanId(val);
                            localStorage.setItem('current_sim_loan_id', val);
                            setShowNewForm(false);
                        }}>
                            <SelectTrigger className="w-[280px] bg-slate-50 border-none font-bold">
                                <SelectValue placeholder="Select a Simulation" />
                            </SelectTrigger>
                            <SelectContent>
                                {loanList.map(l => (
                                    <SelectItem key={l.loan_id} value={l.loan_id} className="font-medium">
                                        {l.loan_id} - ₹{Number(l.amount).toLocaleString()}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Button variant="ghost" size="icon" onClick={() => {
                            setLoanId(null);
                            setShowNewForm(true);
                        }} className="text-primary hover:bg-primary/5">
                            <Plus className="h-5 w-5" />
                        </Button>
                    </div>

                    {loanId && (
                        <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-slate-500 font-mono text-[10px]">
                                {loanId}
                            </Badge>
                            <Button variant="ghost" size="icon" onClick={handleDeleteLoan} className="text-rose-500 hover:bg-rose-50 hover:text-rose-600">
                                <Trash2 className="h-4 w-4" />
                            </Button>
                        </div>
                    )}
                </div>

                {/* Top Navigation Sliders (Restricted to max 10% screen height) */}
                <div className="bg-slate-900 text-white rounded-xl shadow-2xl p-3 flex flex-col md:flex-row gap-6 items-center h-[10%] min-h-[90px] border border-slate-800 overflow-hidden">
                    {/* Day Slider */}
                    <div className="flex-1 w-full space-y-1">
                        <div className="flex justify-between items-center px-1">
                            <span className="text-[9px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-1">
                                <Calendar className="h-2.5 w-2.5" /> Timeline
                            </span>
                            <Badge className="bg-primary hover:bg-primary text-[9px] h-4">T + {dayOffset}</Badge>
                        </div>
                        <Slider
                            value={[dayOffset]}
                            max={liveState?.t_plus || 0}
                            step={1}
                            onValueChange={(val) => setDayOffset(val[0])}
                            className="[&_[role=slider]]:bg-primary [&_[role=slider]]:border-primary [&>[role=track]]:h-1"
                        />
                    </div>

                    <Separator orientation="vertical" className="hidden md:block h-8 bg-slate-800" />

                    {/* Time Slider */}
                    <div className="flex-1 w-full space-y-1">
                        <div className="flex justify-between items-center px-1">
                            <span className="text-[9px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-1">
                                <Clock className="h-2.5 w-2.5" /> Time Scrubber
                            </span>
                            <Badge variant="outline" className="text-white border-slate-700 text-[9px] h-4">
                                {formatInTimeZone(addHours(startOfDay(new Date()), timeOffset), 'Asia/Kolkata', 'hh:mm aa')}
                            </Badge>
                        </div>
                        <Slider
                            value={[timeOffset]}
                            max={23}
                            step={1}
                            onValueChange={(val) => setTimeOffset(val[0])}
                            className="[&_[role=slider]]:bg-slate-300 [&_[role=slider]]:border-slate-300 [&>[role=track]]:h-1"
                        />
                    </div>
                </div>

                {!loanId || showNewForm ? (
                    <div className="flex-1 flex flex-col items-center justify-center text-center bg-white rounded-2xl border border-dashed border-slate-200 shadow-inner p-12">
                        <div className="w-20 h-20 bg-primary/10 rounded-3xl flex items-center justify-center mb-6 rotate-3">
                            <Zap className="h-10 w-10 text-primary" />
                        </div>
                        <h2 className="text-3xl font-black mb-2 tracking-tight">New Loan Simulation</h2>
                        <p className="text-slate-500 max-w-sm mb-10 text-sm font-medium">
                            Define your starting parameters to launch a new time-correlated simulation environment.
                        </p>

                        <div className="max-w-xs w-full space-y-6">
                            <div className="space-y-2 text-left">
                                <Label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Capital Disbursement (₹)</Label>
                                <div className="relative">
                                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-300" />
                                    <Input
                                        type="number"
                                        value={disburseAmount}
                                        onChange={(e) => setDisburseAmount(e.target.value)}
                                        className="h-14 pl-10 text-xl font-black border-2 focus-visible:border-primary transition-all rounded-xl shadow-sm"
                                        placeholder="10,000"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2 text-left">
                                    <Label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">ROI (% p.a.)</Label>
                                    <Input
                                        type="number"
                                        value={roi}
                                        onChange={(e) => setRoi(e.target.value)}
                                        className="h-14 text-xl font-black border-2 focus-visible:border-primary transition-all rounded-xl shadow-sm"
                                        placeholder="12"
                                    />
                                </div>
                                <div className="space-y-2 text-left">
                                    <Label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Duration (Days)</Label>
                                    <Input
                                        type="number"
                                        value={tenure}
                                        onChange={(e) => setTenure(e.target.value)}
                                        className="h-14 text-xl font-black border-2 focus-visible:border-primary transition-all rounded-xl shadow-sm"
                                        placeholder="10"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2 text-left">
                                <Label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Customer Phone (Optional)</Label>
                                <Input
                                    type="text"
                                    placeholder="98765XXXXX"
                                    value={targetCustomerPhone}
                                    onChange={(e) => setTargetCustomerPhone(e.target.value)}
                                    className="h-14 text-xl font-black border-2 focus-visible:border-primary transition-all rounded-xl shadow-sm"
                                />
                                <p className="text-[10px] text-slate-500 ml-1">Defaults to your phone if empty</p>
                            </div>

                            <Button size="lg" onClick={handleDisburse} disabled={loading} className="w-full h-14 text-lg font-black rounded-xl shadow-[0_10px_20px_-10px_rgba(var(--primary),0.5)]">
                                {loading ? 'CALCULATING...' : 'LAUNCH SIMULATION'}
                            </Button>

                            {loanId && (
                                <Button variant="ghost" onClick={() => setShowNewForm(false)} className="text-slate-400 font-bold hover:text-slate-600">
                                    Back to current
                                </Button>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="flex-1 flex flex-col lg:flex-row gap-6 overflow-hidden">

                        {/* Left Column (66%) */}
                        <div className="lg:w-[66%] flex flex-col h-full overflow-hidden">
                            <ScrollArea className="flex-1 pr-4">
                                <div className="flex flex-col gap-6 pb-6">
                                    {/* State Card (Changes with View Slider) */}
                                    <Card className={cn(
                                        "border-none shadow-xl transition-all duration-300 overflow-hidden",
                                        isHistorical ? "bg-amber-50 ring-2 ring-amber-200/50" : "bg-white"
                                    )}>
                                        <div className={cn("h-1.5 w-full", isHistorical ? "bg-amber-400" : "bg-primary")} />
                                        <CardHeader className="pb-4 py-6">
                                            <div className="flex justify-between items-start">
                                                <div>
                                                    <CardTitle className="text-2xl font-black tracking-tighter flex items-center gap-2">
                                                        Account Status Log
                                                        {isHistorical && <Badge className="bg-amber-400 font-black text-[10px] ml-2">HISTORICAL</Badge>}
                                                    </CardTitle>
                                                    <CardDescription className="font-medium text-slate-400">
                                                        Reconstructed state for {formatInTimeZone(viewState ? new Date(viewState.as_of) : new Date(), 'Asia/Kolkata', 'dd MMMM yyyy, hh:mm aa')}
                                                    </CardDescription>
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">System Status</p>
                                                    <p className="text-lg font-black text-slate-900">{viewState?.status || 'Active'}</p>
                                                </div>
                                            </div>
                                        </CardHeader>
                                        <CardContent className="pb-8">
                                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
                                                {[
                                                        { label: 'Net Cash Out', value: viewState?.pos, color: 'text-slate-900', icon: Play },
                                                        { label: 'Real-time Interest', value: viewState?.interest_due, color: 'text-indigo-600' },
                                                        { label: 'Accrued Fees', value: viewState?.fees_due, color: 'text-purple-600' },
                                                        { label: 'PI Overdue', value: (viewState?.overdue_principal ?? 0) + (viewState?.overdue_interest ?? 0), color: viewState?.is_overdue ? 'text-rose-500' : 'text-slate-900' },
                                                ].map((stat, i) => {
                                                    const numericValue = Number(stat.value || 0);
                                                    const intPart = Math.floor(numericValue);
                                                    const decPart = (numericValue % 1).toFixed(2).split('.')[1];
                                                    return (
                                                        <div key={i} className="relative group">
                                                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{stat.label}</p>
                                                            <div className="flex items-baseline gap-1">
                                                                <span className={cn("text-4xl font-black tracking-tighter", stat.color)}>
                                                                    ₹{intPart.toLocaleString('en-IN')}
                                                                </span>
                                                                <span className={cn("text-xs font-bold opacity-30", stat.color)}>
                                                                    .{decPart}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </CardContent>
                                    </Card>

                                    {/* Immediate Customer Actions Card */}
                                    <div className={cn("space-y-4 transition-all", isHistorical ? "opacity-30 pointer-events-none" : "opacity-100")}>
                                        <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 flex items-center gap-2">
                                            <Zap className="h-3 w-3" /> Execute Customer Action (Immediate)
                                        </h3>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            {(() => {
                                                const totalDue = viewState?.total_due || 0;
                                                const hasDemand = viewState?.has_demand || false;
                                                // When demand is raised, hide cancel and show foreclose regardless of t_plus
                                                const showForeclose = hasDemand || (liveState?.t_plus || 0) > 3;
                                                const showCancel = !hasDemand && (liveState?.t_plus || 0) <= 3;
                                                
                                                return (
                                                    <>
                                                        {showForeclose && totalDue > 0 && (
                                                            <Button
                                                                variant="outline"
                                                                className="h-20 flex flex-col gap-1 border-emerald-100 hover:bg-emerald-50 hover:text-emerald-700 font-black text-xs"
                                                                onClick={() => {
                                                                    setModalAction('PAY');
                                                                    setIsModalOpen(true);
                                                                }}
                                                                disabled={loading}
                                                            >
                                                                <DollarSign className="h-5 w-5 text-emerald-500" />
                                                                PAY TOTAL DUE
                                                            </Button>
                                                        )}
                                                        {showCancel && (
                                                            <Button
                                                                variant="outline"
                                                                className="h-20 flex flex-col gap-1 border-rose-100 hover:bg-rose-50 hover:text-rose-700 font-black text-xs"
                                                                onClick={() => {
                                                                    setModalAction('CANCEL');
                                                                    setIsModalOpen(true);
                                                                }}
                                                                disabled={loading}
                                                            >
                                                                <XCircle className="h-5 w-5 text-rose-500" />
                                                                CANCEL LOAN
                                                                <span className="text-[8px] font-bold opacity-50">T+0 TO T+3</span>
                                                            </Button>
                                                        )}
                                                        {showForeclose && (
                                                            <Button
                                                                variant="outline"
                                                                className="h-20 flex flex-col gap-1 border-indigo-100 hover:bg-indigo-50 hover:text-indigo-700 font-black text-xs"
                                                                onClick={() => {
                                                                    setModalAction('FORECLOSE');
                                                                    setIsModalOpen(true);
                                                                }}
                                                                disabled={loading}
                                                            >
                                                                <ArrowRight className="h-5 w-5 text-indigo-500" />
                                                                FORECLOSE
                                                                <span className="text-[8px] font-bold opacity-50">{hasDemand ? 'DEMAND RAISED' : 'T+4 ONWARDS'}</span>
                                                            </Button>
                                                        )}
                                                    </>
                                                );
                                            })()}
                                        </div>
                                    </div>

                                    {/* Event Planner (Disabled in Historical View) */}
                                    <div className={cn("space-y-4 transition-all", isHistorical ? "opacity-30 scale-[0.98] pointer-events-none grayscale" : "opacity-100")}>
                                        <div className="flex items-center justify-between px-1">
                                            <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 flex items-center gap-2">
                                                <Settings className="h-3 w-3" /> Intra-day Orchestrator (EOD Planned)
                                            </h3>
                                            <Badge variant="outline" className="text-[9px] font-bold text-slate-400 border-slate-200">
                                                LIVE SESSION
                                            </Badge>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            {/* Payment Toggle */}
                                            {(() => {
                                                const totalDue = viewState?.total_due || 0;
                                                return (liveState?.t_plus || 0) > 3 && totalDue > 0 && (
                                                    <Card className="shadow-lg border-none bg-emerald-50/30 ring-1 ring-emerald-500/10">
                                                        <CardHeader className="p-4 pb-0 flex flex-row items-center justify-between">
                                                            <Label className="font-black text-xs flex items-center gap-2 text-emerald-700">
                                                                <div className="p-1 bg-emerald-500 rounded-md shadow-lg shadow-emerald-200">
                                                                    <DollarSign className="h-3 w-3 text-white" />
                                                                </div>
                                                                PLAN PAY
                                                            </Label>
                                                            <Switch
                                                                checked={plannedEvents.payment.enabled}
                                                                onCheckedChange={(c) => setPlannedEvents(p => ({ ...p, payment: { ...p.payment, enabled: c } }))}
                                                                className="data-[state=checked]:bg-emerald-500"
                                                            />
                                                        </CardHeader>
                                                        <CardContent className="p-4 pt-4 space-y-4">
                                                            <Input
                                                                placeholder="₹ Amount"
                                                                value={plannedEvents.payment.amount}
                                                                onChange={(e) => setPlannedEvents(p => ({ ...p, payment: { ...p.payment, amount: e.target.value } }))}
                                                                disabled={!plannedEvents.payment.enabled}
                                                                className="h-10 text-sm font-bold bg-white border-emerald-100 rounded-lg"
                                                            />
                                                            <div className="flex items-center gap-2 px-1">
                                                                <Clock className="h-3 w-3 text-emerald-400" />
                                                                <Input
                                                                    type="time"
                                                                    value={plannedEvents.payment.time}
                                                                    onChange={(e) => setPlannedEvents(p => ({ ...p, payment: { ...p.payment, time: e.target.value } }))}
                                                                    disabled={!plannedEvents.payment.enabled}
                                                                    className="h-8 text-[10px] font-black bg-white border-emerald-100 p-1 w-full"
                                                                />
                                                            </div>
                                                        </CardContent>
                                                    </Card>
                                                );
                                            })()}

                                            {/* Cancel Toggle - hidden when demand is raised */}
                                            {!(viewState?.has_demand) && (liveState?.t_plus || 0) <= 3 && (
                                                <Card className="shadow-lg border-none bg-rose-50/30 ring-1 ring-rose-500/10">
                                                    <CardHeader className="p-4 pb-0 flex flex-row items-center justify-between">
                                                        <Label className="font-black text-xs flex items-center gap-2 text-rose-700">
                                                            <div className="p-1 bg-rose-500 rounded-md shadow-lg shadow-rose-200">
                                                                <XCircle className="h-3 w-3 text-white" />
                                                            </div>
                                                            PLAN CANCEL
                                                        </Label>
                                                        <Switch
                                                            checked={plannedEvents.cancel.enabled}
                                                            onCheckedChange={(c) => setPlannedEvents(p => ({ ...p, cancel: { ...p.cancel, enabled: c } }))}
                                                            className="data-[state=checked]:bg-rose-500"
                                                        />
                                                    </CardHeader>
                                                    <CardContent className="p-4 pt-4 space-y-3">
                                                        <div className="flex items-center gap-2 px-1">
                                                            <Clock className="h-3 w-3 text-rose-400" />
                                                            <Input
                                                                type="time"
                                                                value={plannedEvents.cancel.time}
                                                                onChange={(e) => setPlannedEvents(p => ({ ...p, cancel: { ...p.cancel, time: e.target.value } }))}
                                                                disabled={!plannedEvents.cancel.enabled}
                                                                className="h-8 text-[10px] font-black bg-white border-rose-100 p-1 w-full"
                                                            />
                                                        </div>
                                                        <p className="text-[9px] font-bold text-rose-400 tracking-tighter text-center">T+0 TO T+3 ONLY</p>
                                                    </CardContent>
                                                </Card>
                                            )}

                                            {/* Foreclose Toggle - visible when demand is raised OR t_plus > 3 */}
                                            {(viewState?.has_demand || (liveState?.t_plus || 0) > 3) && (
                                                <Card className="shadow-lg border-none bg-indigo-50/30 ring-1 ring-indigo-500/10">
                                                    <CardHeader className="p-4 pb-0 flex flex-row items-center justify-between">
                                                        <Label className="font-black text-xs flex items-center gap-2 text-indigo-700">
                                                            <div className="p-1 bg-indigo-500 rounded-md shadow-lg shadow-indigo-200">
                                                                <ArrowRight className="h-3 w-3 text-white" />
                                                            </div>
                                                            PLAN FORCLS
                                                        </Label>
                                                        <Switch
                                                            checked={plannedEvents.foreclose.enabled}
                                                            onCheckedChange={(c) => setPlannedEvents(p => ({ ...p, foreclose: { ...p.foreclose, enabled: c } }))}
                                                            className="data-[state=checked]:bg-indigo-500"
                                                        />
                                                    </CardHeader>
                                                    <CardContent className="p-4 pt-4 space-y-3">
                                                        <div className="flex items-center gap-2 px-1">
                                                            <Clock className="h-3 w-3 text-indigo-400" />
                                                            <Input
                                                                type="time"
                                                                value={plannedEvents.foreclose.time}
                                                                onChange={(e) => setPlannedEvents(p => ({ ...p, foreclose: { ...p.foreclose, time: e.target.value } }))}
                                                                disabled={!plannedEvents.foreclose.enabled}
                                                                className="h-8 text-[10px] font-black bg-white border-indigo-100 p-1 w-full"
                                                            />
                                                        </div>
                                                        <p className="text-[9px] font-bold text-indigo-400 tracking-tighter text-center">{viewState?.has_demand ? 'DEMAND RAISED' : 'T+4 ONWARDS'}</p>
                                                    </CardContent>
                                                </Card>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </ScrollArea>

                            {/* EOD Controller - Fixed at bottom */}
                            <div className="pt-4 border-t border-slate-100 mt-auto">
                                <Card className={cn("shadow-2xl border-none transition-all", isHistorical && "opacity-30 grayscale pointer-events-none")}>
                                    <CardHeader className="bg-slate-50 px-6 py-4 flex flex-row items-center justify-between border-b">
                                        <div className="flex-1">
                                            <CardTitle className="text-sm font-black tracking-tight text-slate-700">SIMULATION ENGINE</CardTitle>
                                            <CardDescription className="text-[10px] font-medium text-slate-400 uppercase tracking-widest">SOD / EOD Cycle Controller</CardDescription>
                                        </div>
                                        <div className="flex gap-2">
                                            <Button 
                                                variant="outline"
                                                size="sm"
                                                onClick={runSOD}
                                                disabled={sodProcessing || eodProcessing}
                                                className="bg-white border-slate-200 text-slate-600 font-black text-[10px] h-9 px-4 rounded-xl shadow-sm hover:bg-slate-50"
                                            >
                                                {sodProcessing ? 'SOD...' : 'RUN SOD'}
                                            </Button>
                                            <Button 
                                                onClick={runEOD}
                                                disabled={eodProcessing || sodProcessing}
                                                className="bg-primary hover:bg-primary/90 text-white font-black text-[10px] h-9 px-4 rounded-xl shadow-[0_4px_10px_-4px_rgba(var(--primary),0.5)]"
                                            >
                                                {eodProcessing ? 'EOD...' : 'RUN EOD & NEXT DAY'}
                                            </Button>
                                        </div>
                                    </CardHeader>
                                    <CardContent className="p-6">
                                        <div className="flex flex-col gap-6">
                                            <div className="flex flex-col gap-4">
                                                <div className="flex items-center justify-between">
                                                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-1">
                                                        <Zap className="h-2.5 w-2.5" /> Demand Execution (Today)
                                                    </span>
                                                    <div className="flex items-center gap-2">
                                                        <Label htmlFor="demand-status" className="text-[10px] font-black uppercase text-slate-400">Status (8PM IST):</Label>
                                                        <Select value={demandStatus} onValueChange={(val: 'PASS' | 'FAIL') => setDemandStatus(val)}>
                                                            <SelectTrigger id="demand-status" className="h-7 w-[80px] text-[10px] font-black bg-white border-slate-200">
                                                                <SelectValue />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                <SelectItem value="PASS" className="text-[10px] font-black text-emerald-600">PASS</SelectItem>
                                                                <SelectItem value="FAIL" className="text-[10px] font-black text-rose-600">FAIL</SelectItem>
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-3">
                                                <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-1 shrink-0">
                                                    <Clock className="h-2.5 w-2.5" /> Execution Time
                                                </span>
                                                <Input
                                                    type="time"
                                                    value={demandTime}
                                                    onChange={(e) => setDemandTime(e.target.value)}
                                                    className="h-7 w-[110px] text-[11px] font-black bg-white border-slate-200 px-2"
                                                />
                                                <span className="text-[9px] font-bold text-slate-400 shrink-0">IST</span>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>
                        </div>

                        {/* Right Column (Chronological Log) */}
                        <div className="lg:w-[33%] flex flex-col h-full overflow-hidden">
                            <Card className="h-full border-none shadow-2xl flex flex-col bg-slate-900 text-slate-100 rounded-3xl overflow-hidden">
                                <CardHeader className="bg-slate-800/80 p-6 border-b border-slate-700/50">
                                    <CardTitle className="text-xl font-black tracking-tight flex items-center gap-2 text-white">
                                        <History className="h-5 w-5 text-primary" />
                                        EVENT FEED
                                    </CardTitle>
                                    <CardDescription className="text-slate-500 text-[10px] font-bold uppercase tracking-widest">
                                        Atomic audit of simulation delta
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="p-0 flex-1 overflow-hidden relative">
                                    <ScrollArea className="h-full p-6">
                                        <div className="space-y-8 relative ml-4 border-l-2 border-slate-800 pl-8 pb-12">
                                            {logs.map((log, idx) => (
                                                <div key={idx} className="relative group">
                                                    <div className={cn(
                                                        "absolute -left-[41px] top-1.5 h-4 w-4 rounded-full border-4 border-slate-900 transition-all z-10",
                                                        log.type === 'TRANSACTION' ? "bg-emerald-400 shadow-[0_0_15px_rgba(52,211,153,0.3)]" :
                                                            log.type === 'DEMAND' ? "bg-amber-400 shadow-[0_0_15px_rgba(251,191,36,0.3)]" : "bg-primary shadow-[0_0_15px_rgba(var(--primary),0.3)]"
                                                    )} />

                                                    <div className="space-y-2">
                                                        <div className="flex items-center justify-between">
                                                            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">
                                                                {formatInTimeZone(new Date(log.date), 'Asia/Kolkata', 'dd MMM')} at {formatInTimeZone(new Date(log.date), 'Asia/Kolkata', 'HH:mm')}
                                                            </span>
                                                            <Badge className={cn(
                                                                "text-[8px] font-black h-4 py-0 px-2 leading-none border-none rounded-full",
                                                                log.type === 'TRANSACTION' ? "bg-emerald-400/10 text-emerald-400" :
                                                                    log.type === 'DEMAND' ? "bg-amber-400/10 text-amber-400" : "bg-primary/10 text-primary"
                                                            )}>
                                                                {log.type}
                                                            </Badge>
                                                        </div>
                                                        <p className="text-base font-black text-slate-100 leading-tight tracking-tight uppercase">
                                                            {log.detail}
                                                        </p>
                                                        <div className="flex items-center justify-between bg-slate-800/30 p-2 rounded-lg">
                                                            <p className="text-[9px] text-slate-400 font-bold">
                                                                <span className="text-slate-600 mr-1">ORIGIN:</span> {log.cause}
                                                            </p>
                                                            <p className="text-[8px] text-slate-700 font-mono">
                                                                {formatInTimeZone(new Date(log.timestamp), 'Asia/Kolkata', 'HH:mm:ss')}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </ScrollArea>
                                    <div className="absolute bottom-0 left-0 w-full h-24 bg-gradient-to-t from-slate-900 via-slate-900/80 to-transparent pointer-events-none" />
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                )}
            </div>

            <PaymentModal
                isOpen={isModalOpen}
                initialAction={modalAction}
                onClose={() => setIsModalOpen(false)}
                loan={liveState}
                onPaymentSuccess={() => loanId && fetchLiveState(loanId)}
            />

            <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 5px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #e2e8f0;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #cbd5e1;
        }
      `}</style>
        </StaffLayout>
    );
};

export default SimulationDashboard;
